import { ApolloLink } from '@apollo/client/link/core/ApolloLink'
import { ApolloClient } from '@apollo/client/core/ApolloClient'
import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache'
import { HttpLink } from '@apollo/client/link/http/HttpLink'
import { AUTH_TYPE, AuthOptions } from 'aws-appsync-auth-link/lib/auth-link'
import { createAuthLink } from 'aws-appsync-auth-link'
/* import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist' */
import { getJwtToken } from '../lib/firebase'
import { NormalizedCacheObject } from '@apollo/client'

const url = process.env.NEXT_PUBLIC_APPSYNC_URL
const region = process.env.NEXT_PUBLIC_APPSYNC_REGION

let client: ApolloClient<NormalizedCacheObject> | null = null

// -----------------------------------------------------------------------------
// getDummyApolloClient
//
// Purpose:
// Creates a temporary Apollo Client instance during the first server-side render
// or when the real Apollo Client is not yet initialized.
//
// Why:
// In React, hooks like `useQuery` must always be called inside a valid ApolloProvider.
// Returning `null` from AppProviders until the real Apollo Client is ready would break
// server-side rendering and cause errors when any Apollo hooks run.
//
// How it solves the problem:
// - Provides a minimal Apollo Client immediately so the app can server-render properly.
// - Prevents crashes from Apollo hooks firing before the real client is ready.
// - Uses ApolloLink.empty() to ensure no network requests are made with this dummy.
// - Ensures SEO-critical tags (like <link rel="alternate"> in <Head>) are correctly
//   rendered server-side and visible in "View Source" and SEO tools.
//
// This dummy client is automatically replaced with the real Apollo Client on the client side.
// -----------------------------------------------------------------------------
export const getDummyApolloClient = () =>
  new ApolloClient({
    link: ApolloLink.empty(),
    cache: new InMemoryCache(),
    ssrMode: true, // Important! Required so it does not break SSR
  })

export const getApolloClient = async (): Promise<ApolloClient<NormalizedCacheObject>> => {
  if (client) {
    return client
  }

  const auth: AuthOptions = {
    type: AUTH_TYPE.OPENID_CONNECT,
    jwtToken: getJwtToken,
  }

  const link = ApolloLink.from([
    createAuthLink({ url, region, auth }),
    new HttpLink({ uri: url, fetch }), // handles GraphQL over HTTP
  ])

  client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    ssrMode: typeof window === 'undefined', // important for SSR
  })

  return client
}
